'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { UseFullscreenReturn } from '@/contexts/games/types';

/**
 * Хук для управления полноэкранным режимом
 * Поддерживает все современные браузеры и обрабатывает различия в API
 */
export function useFullscreen(targetElement?: HTMLElement | null): UseFullscreenReturn {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const elementRef = useRef<HTMLElement | null>(null);

  // Проверяем поддержку Fullscreen API
  useEffect(() => {
    const checkSupport = () => {
      // Проверяем стандартный API
      if (document.fullscreenEnabled) return true;

      // Проверяем vendor prefixes
      if ((document as any).webkitFullscreenEnabled) return true;
      if ((document as any).mozFullScreenEnabled) return true;
      if ((document as any).msFullscreenEnabled) return true;

      // Для iOS проверяем наличие методов на элементе
      const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
      if (isIOS) {
        // На iOS проверяем наличие webkitRequestFullscreen на document.documentElement
        const element = document.documentElement;
        return !!(
          (element as any).webkitRequestFullscreen ||
          (element as any).webkitEnterFullscreen
        );
      }

      return false;
    };

    setIsSupported(checkSupport());
  }, []);

  // Устанавливаем целевой элемент
  useEffect(() => {
    if (targetElement) {
      elementRef.current = targetElement;
    } else {
      // Если элемент не передан, используем document.documentElement
      elementRef.current = document.documentElement;
    }
  }, [targetElement]);

  // Получаем текущий полноэкранный элемент
  const getFullscreenElement = useCallback((): Element | null => {
    // Проверяем стандартный API и vendor prefixes
    const fullscreenEl = (
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).webkitCurrentFullScreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement ||
      null
    );

    // Для iOS видео элементов проверяем webkitDisplayingFullscreen
    if (!fullscreenEl && elementRef.current) {
      const element = elementRef.current as any;
      if (element.webkitDisplayingFullscreen) {
        return element;
      }
    }

    return fullscreenEl;
  }, []);

  // Проверяем, находимся ли мы в полноэкранном режиме
  const checkIsFullscreen = useCallback((): boolean => {
    const fullscreenElement = getFullscreenElement();
    return fullscreenElement === elementRef.current;
  }, [getFullscreenElement]);

  // Вход в полноэкранный режим
  const enter = useCallback(async (): Promise<void> => {
    if (!isSupported || !elementRef.current) {
      console.warn('Fullscreen is not supported or element is not available');
      return;
    }

    try {
      const element = elementRef.current;

      // Standard API
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      }
      // Webkit (Safari, iOS)
      else if ((element as any).webkitRequestFullscreen) {
        // iOS Safari требует специальный параметр
        const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
        if (isIOS) {
          // На iOS используем webkitEnterFullscreen если доступен (для видео)
          // или webkitRequestFullscreen с параметром для других элементов
          if (typeof (element as any).webkitEnterFullscreen === 'function') {
            (element as any).webkitEnterFullscreen();
          } else {
            // Для iOS нужен параметр Element.ALLOW_KEYBOARD_INPUT
            (element as any).webkitRequestFullscreen(
              (Element as any).ALLOW_KEYBOARD_INPUT || 1
            );
          }
        } else {
          await (element as any).webkitRequestFullscreen();
        }
      }
      // Mozilla
      else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      }
      // IE/Edge
      else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      }
      else {
        console.warn('No fullscreen method available');
        return;
      }
    } catch (error) {
      console.error('Failed to enter fullscreen:', error);
      // Don't throw - just log the error and continue
    }
  }, [isSupported]);

  // Выход из полноэкранного режима
  const exit = useCallback(async (): Promise<void> => {
    if (!isSupported) {
      console.warn('Fullscreen is not supported');
      return;
    }

    try {
      // Standard API
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
      // Webkit (Safari, iOS)
      else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      }
      // iOS может использовать webkitCancelFullScreen
      else if ((document as any).webkitCancelFullScreen) {
        await (document as any).webkitCancelFullScreen();
      }
      // Для видео элементов на iOS
      else if (elementRef.current && typeof (elementRef.current as any).webkitExitFullscreen === 'function') {
        (elementRef.current as any).webkitExitFullscreen();
      }
      // Mozilla
      else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      }
      // IE/Edge
      else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
      else {
        console.warn('No exit fullscreen method available');
        return;
      }
    } catch (error) {
      console.error('Failed to exit fullscreen:', error);
      // Don't throw - just log the error and continue
    }
  }, [isSupported]);

  // Переключение полноэкранного режима
  const toggle = useCallback(async (): Promise<void> => {
    if (isFullscreen) {
      await exit();
    } else {
      await enter();
    }
  }, [isFullscreen, enter, exit]);

  // Обработчик изменения полноэкранного режима
  const handleFullscreenChange = useCallback(() => {
    setIsFullscreen(checkIsFullscreen());
  }, [checkIsFullscreen]);

  // Обработчик ошибок полноэкранного режима
  const handleFullscreenError = useCallback((event: Event) => {
    console.error('Fullscreen error:', event);
    setIsFullscreen(false);
  }, []);

  // Подписываемся на события изменения полноэкранного режима
  useEffect(() => {
    if (!isSupported) return;

    // Список событий для разных браузеров
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'msfullscreenchange',
    ];

    const errorEvents = [
      'fullscreenerror',
      'webkitfullscreenerror',
      'mozfullscreenerror',
      'msfullscreenerror',
    ];

    // Добавляем обработчики событий
    events.forEach(event => {
      document.addEventListener(event, handleFullscreenChange);
    });

    errorEvents.forEach(event => {
      document.addEventListener(event, handleFullscreenError);
    });

    // Проверяем начальное состояние
    setIsFullscreen(checkIsFullscreen());

    // Очистка при размонтировании
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFullscreenChange);
      });

      errorEvents.forEach(event => {
        document.removeEventListener(event, handleFullscreenError);
      });
    };
  }, [isSupported, handleFullscreenChange, handleFullscreenError, checkIsFullscreen]);

  // Обработка клавиши Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        exit().catch(console.error);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isFullscreen, exit]);

  return {
    isFullscreen,
    isSupported,
    toggle,
    enter,
    exit,
    element: elementRef.current,
  };
}
